import { scoreSamples } from './scoreSamples';

export const SCORE_ABC = scoreSamples;

export const SCORE_DRUMS: Array<[string, string]> = [
  ['Crash', '#ef4455'],
  ['Tom 1', '#ff8a22'],
  ['Tom 2', '#ffd323'],
  ['Ride', '#35b85f'],
  ['Snare', '#3185ee'],
  ['Hi-hat', '#ef55a5'],
  ['Floor', '#8246d9'],
  ['Kick', '#8d97a3'],
];

const DRUM_MAP: Record<string, number> = {
  a: 0,
  e: 1,
  d: 2,
  '^f': 3,
  '=f': 3,
  c: 4,
  '^c': 4,
  g: 5,
  '^g': 5,
  '=g': 5,
  D: 5,
  A: 6,
  F: 7,
};

export interface ScoreEvent {
  position: number;
  drumIndex?: number;
  voice: 'hands' | 'feet';
  rest?: boolean;
  durationBeats: number;
}

export interface ScoreMeasure {
  index: number;
  startStep: number;
  slots: number;
  events: ScoreEvent[];
}

export interface ScoreTimelineStep {
  step: number;
  startBeat: number;
  durationBeats: number;
}

export function parseAbcBpm(source: string): number {
  const explicit = String(source || '').match(/Q:\s*(\d+)\s*\/\s*(\d+)\s*=\s*(\d+)/);
  if (explicit) {
    const noteValue = Number(explicit[1]) / Number(explicit[2]);
    const quartersPerMinute = Number(explicit[3]) * (noteValue / 0.25);
    return Number.isFinite(quartersPerMinute) && quartersPerMinute > 0 ? quartersPerMinute : 93;
  }
  const bare = String(source || '').match(/Q:\s*(\d+)/);
  const bpm = Number(bare?.[1]);
  return Number.isFinite(bpm) && bpm > 0 ? bpm : 93;
}

function parseAbcUnitBeats(source: string): number {
  const match = String(source || '').match(/^L:\s*(\d+)\s*\/\s*(\d+)/m);
  if (!match) return 1;
  const unitBeats = (Number(match[1]) / Number(match[2])) / 0.25;
  return Number.isFinite(unitBeats) && unitBeats > 0 ? unitBeats : 1;
}

function readAbcDuration(bar: string, index: number, unitBeats: number): { beats: number; nextIndex: number } {
  let cursor = index;
  let numerator = 0;
  let sawNumber = false;
  while (cursor < bar.length && bar[cursor] >= '0' && bar[cursor] <= '9') {
    sawNumber = true;
    numerator = numerator * 10 + (bar.charCodeAt(cursor) - 48);
    cursor += 1;
  }

  if (bar[cursor] === '/') {
    cursor += 1;
    let slashes = 1;
    while (bar[cursor] === '/') {
      slashes += 1;
      cursor += 1;
    }
    let denominator = 0;
    while (cursor < bar.length && bar[cursor] >= '0' && bar[cursor] <= '9') {
      denominator = denominator * 10 + (bar.charCodeAt(cursor) - 48);
      cursor += 1;
    }
    if (slashes > 1 && denominator === 0) {
      return { beats: unitBeats / (2 ** slashes), nextIndex: cursor };
    }
    return { beats: unitBeats * (sawNumber ? numerator : 1) / (denominator || 2), nextIndex: cursor };
  }

  if (sawNumber) return { beats: unitBeats * numerator, nextIndex: cursor };
  return { beats: unitBeats, nextIndex: cursor };
}

function readVoiceLines(source: string, voiceName: string): string[] {
  const lines = source.split(/\r?\n/);
  const start = lines.findIndex((line) => line.trim().startsWith(`V:${voiceName}`));
  if (start < 0) return [];
  const body: string[] = [];
  for (let index = start + 1; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (line.startsWith('V:')) break;
    if (!line || line.startsWith('%%') || line.startsWith('%') || /^[wW]:/.test(line)) continue;
    body.push(line);
  }
  return body;
}

function parseChordNotes(chord: string, position: number, voice: 'hands' | 'feet', durationBeats: number): ScoreEvent[] {
  const events: ScoreEvent[] = [];
  for (let chordIndex = 0; chordIndex < chord.length; chordIndex += 1) {
    const chordChar = chord[chordIndex];
    if (chordChar === ' ' || chordChar === '/') continue;
    if (chordChar === '^' || chordChar === '=') {
      const token = chordChar + chord[chordIndex + 1];
      const drumIndex = DRUM_MAP[token] ?? DRUM_MAP[token.replace('=', '')];
      if (Number.isInteger(drumIndex)) events.push({ position, drumIndex, voice, durationBeats });
      chordIndex += 1;
      continue;
    }
    const drumIndex = DRUM_MAP[chordChar];
    if (Number.isInteger(drumIndex)) events.push({ position, drumIndex, voice, durationBeats });
  }
  return events;
}

function parseMeasureTokens(bar: string, voice: 'hands' | 'feet', unitBeats: number): { events: ScoreEvent[]; slots: number } {
  const events: ScoreEvent[] = [];
  let position = 0;
  for (let index = 0; index < bar.length; index += 1) {
    const char = bar[index];
    if (char === ' ' || char === '\t') continue;
    if (char === '[') {
      const close = bar.indexOf(']', index);
      const duration = readAbcDuration(bar, close >= 0 ? close + 1 : index + 1, unitBeats);
      const chord = close >= 0 ? bar.slice(index + 1, close) : '';
      const chordEvents = parseChordNotes(chord, position, voice, duration.beats);
      if (chordEvents.length) events.push(...chordEvents);
      else events.push({ position, rest: true, voice, durationBeats: duration.beats });
      position += 1;
      index = duration.nextIndex - 1;
      continue;
    }
    if (char === ']') continue;
    if (char === 'z' || char === 'x') {
      const duration = readAbcDuration(bar, index + 1, unitBeats);
      events.push({ position, rest: true, voice, durationBeats: duration.beats });
      position += 1;
      index = duration.nextIndex - 1;
      continue;
    }
    const token = char === '^' || char === '=' ? char + (bar[index + 1] || '') : char;
    if (char === '^' || char === '=') index += 1;
    const drumIndex = DRUM_MAP[token] ?? DRUM_MAP[token.replace('=', '')];
    if (!Number.isInteger(drumIndex)) continue;
    const duration = readAbcDuration(bar, index + 1, unitBeats);
    events.push({ position, drumIndex, voice, durationBeats: duration.beats });
    position += 1;
    index = duration.nextIndex - 1;
  }
  return { events, slots: Math.max(position, 4) };
}

function cleanBar(bar: string): string {
  let result = bar.replace(/^:+/, '').replace(/:+$/, '').trim();
  while (result.endsWith(']') && (result.match(/\]/g) || []).length > (result.match(/\[/g) || []).length) {
    result = result.slice(0, -1).trim();
  }
  return result;
}

function parseVoiceMeasures(lines: string[], voice: 'hands' | 'feet', unitBeats: number) {
  return lines.join('|')
    .split('|')
    .map((bar) => cleanBar(bar))
    .filter(Boolean)
    .map((bar) => parseMeasureTokens(bar, voice, unitBeats));
}

export function parseAbcMeasures(source: string): ScoreMeasure[] {
  const unitBeats = parseAbcUnitBeats(source);
  const handMeasures = parseVoiceMeasures(readVoiceLines(source, 'Hands'), 'hands', unitBeats);
  const feetMeasures = parseVoiceMeasures(readVoiceLines(source, 'Feet'), 'feet', unitBeats);
  const length = Math.max(handMeasures.length, feetMeasures.length);
  let startStep = 0;
  return Array.from({ length }, (_, index) => {
    const slots = Math.max(handMeasures[index]?.slots || 4, feetMeasures[index]?.slots || 4);
    const measure: ScoreMeasure = {
      index,
      startStep,
      slots,
      events: [
        ...(handMeasures[index]?.events || []),
        ...(feetMeasures[index]?.events || []),
      ].sort((a, b) => a.position - b.position || ((a.drumIndex ?? 99) - (b.drumIndex ?? 99))),
    };
    startStep += slots;
    return measure;
  });
}

export function buildScoreTimeline(measures: ScoreMeasure[]): ScoreTimelineStep[] {
  const steps: ScoreTimelineStep[] = [];
  let startBeat = 0;
  measures.forEach((measure) => {
    for (let position = 0; position < measure.slots; position += 1) {
      const events = measure.events.filter((event) => event.position === position);
      const durationBeats = events.find((event) => event.voice === 'hands' && event.durationBeats > 0)?.durationBeats
        || events.find((event) => event.durationBeats > 0)?.durationBeats
        || 0;
      steps.push({ step: measure.startStep + position, startBeat, durationBeats });
      startBeat += durationBeats;
    }
  });
  return steps;
}

export function stepAtBeat(timeline: ScoreTimelineStep[], beat: number): number {
  if (!timeline.length || beat < 0) return -1;
  for (let index = 0; index < timeline.length; index += 1) {
    const item = timeline[index];
    if (item.durationBeats <= 0) continue;
    if (beat < item.startBeat + item.durationBeats) return item.step;
  }
  return timeline[timeline.length - 1].step;
}

export function prepareAbcForStaff(source: string): string {
  return source
    .split(/\r?\n/)
    .filter((line, index, lines) => {
      const trimmed = line.trim();
      const previous = lines[index - 1]?.trim() || '';
      const next = lines[index + 1]?.trim() || '';
      return !(trimmed === 'zzzz|]' && previous.endsWith('|]') && next.startsWith('V:'));
    })
    .join('\n');
}
